/**
 * Game audio factory used by the engine: procedural sound effects and music (no sample
 * files) on the AudioContext shared with the voice chat (context.ts).
 *
 * Graph: sfx bus ─┐
 *        music players → music bus (volume × ducking) ─┴→ master output chain → speakers
 *
 * - Nothing happens until the shared context runs (the first tap anywhere unlocks it):
 *   `play()` is a no-op, `startMusic()` only remembers the track and starts it once the
 *   context runs. No queue that would later play in a burst.
 * - The music is a lookahead scheduler (music/sequencer.ts) driven by a short timer; while
 *   the page is hidden it pauses (no timer work at all) and continues when visible (a tune on
 *   the game's clock with the next frame, from where the game is by then).
 * - The local racer's music runs on the game's clock (startMusic with a clock, then
 *   syncMusic every frame): it begins with every attempt, its first beat on the attempt's
 *   first tick, as the player hears it (output latency included), and stays on the beat
 *   through turbo, slow motion and lost frames. Without a clock it keeps time on its own.
 * - The music ducks while other players talk in the voice chat (AudioHost.remoteVoiceActive,
 *   set by the voice engine) or when the engine asks for it (setDucked).
 * - `dispose()` stops everything this instance started; the shared context stays.
 */
import { effect, untracked } from '@preact/signals';
import { type AudioHost, audioHost } from './context';
import { DEFAULT_VOLUMES, musicBusGain, sfxBusGain } from './levels';
import { composeSong, type Song } from './music/composer';
import { MusicPlayer } from './music/player';
import { SCHEDULER_LOOKAHEAD, SCHEDULER_TICK_MS } from './music/sequencer';
import { cueKey, cueVoices } from './sfx';
import { createSynthStats, type SynthStats, scheduleVoices } from './synth';
import type { GameAudio, GameAudioFactory, MusicClock, MusicTrack, SoundCue } from './types';

/** The same cue requested again within this time plays once, s. */
const MIN_REPEAT = 0.03;
/** Upper bound of sound-effect voices playing at once (protects weak phones). */
const MAX_SFX_VOICES = 24;
const DEFAULT_STOP_FADE_MS = 600;
/** A new level's music cross-fades with the old one this fast, s. */
const RESTART_FADE = 0.12;
/**
 * A frame reaches the screen about this long after it was drawn: the music of a tick is
 * timed to be heard when the tick is seen, s.
 */
export const DISPLAY_LAG = 0.02;
/** Longest output latency believed (Bluetooth headphones report a lot), s. */
const MAX_OUTPUT_LAG = 0.35;
/**
 * The game's clock moves on at most this much between two frames (the engine's catch-up
 * limit, MAX_CATCH_UP_MS in game/localRun.ts): the furthest the music guesses ahead, ms.
 */
const MAX_CLOCK_GUESS_MS = 100;

/** The engine's clock as last heard, and when (performance.now()). */
interface HeardClock extends MusicClock {
  heardAt: number;
}

function now(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

interface Buses {
  ctx: AudioContext;
  sfx: GainNode;
  music: GainNode;
  noise: AudioBuffer;
}

export const createGameAudio: GameAudioFactory = () =>
  new WebGameAudio(audioHost, typeof document === 'undefined' ? null : document);

export class WebGameAudio implements GameAudio {
  private buses: Buses | null = null;
  private player: MusicPlayer | null = null;
  /** The music the engine wants (null = none), kept while it cannot play yet. */
  private track: MusicTrack | null = null;
  /** The game's clock the music follows (null: it keeps time on its own). */
  private clock: HeardClock | null = null;
  /** Smoothed time between scheduling a sound and hearing it, s (null: not measured yet). */
  private outputLag: number | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private volumes: { sfx: number; music: number } = { ...DEFAULT_VOLUMES };
  private ducked = false;
  private timeScale = 1;
  private disposed = false;
  private readonly lastPlayed = new Map<string, number>();
  private readonly sfxStats: SynthStats = createSynthStats();
  private readonly musicStats: SynthStats = createSynthStats();
  private readonly cleanups: Array<() => void> = [];
  private readonly songs = new Map<string, Song>();

  constructor(
    private readonly host: AudioHost,
    private readonly doc: Document | null,
  ) {
    this.cleanups.push(
      effect(() => {
        const running = host.running.value;
        untracked(() => this.onContextRunning(running));
      }),
      effect(() => {
        const othersTalking = host.remoteVoiceActive.value;
        untracked(() => this.applyMusicGain(othersTalking));
      }),
    );
    if (doc) {
      const onVisibility = () => this.onVisibility();
      doc.addEventListener('visibilitychange', onVisibility);
      this.cleanups.push(() => doc.removeEventListener('visibilitychange', onVisibility));
    }
  }

  /** Counters for measurements: nodes created, voices played and playing. */
  get stats(): { sfx: Readonly<SynthStats>; music: Readonly<SynthStats> } {
    return { sfx: this.sfxStats, music: this.musicStats };
  }

  play(cue: SoundCue): void {
    if (this.disposed || this.hidden()) return;
    const buses = this.ensureBuses();
    if (!buses) return;
    const now = buses.ctx.currentTime;
    const key = cueKey(cue);
    const last = this.lastPlayed.get(key);
    if (last !== undefined && now - last >= 0 && now - last < MIN_REPEAT) return;
    if (this.sfxStats.active >= MAX_SFX_VOICES) return;
    this.lastPlayed.set(key, now);
    scheduleVoices(buses.ctx, buses.sfx, buses.noise, cueVoices(cue), now, 0, this.sfxStats);
  }

  startMusic(track: MusicTrack, clock?: MusicClock): void {
    if (this.disposed) return;
    this.track = {
      bpm: track.bpm,
      theme: track.theme,
      seed: track.seed,
      season: track.season ?? 'none',
      ...(track.boss === undefined ? {} : { boss: track.boss }),
    };
    this.clock = clock ? heard(clock) : null;
    this.startPlayer();
  }

  syncMusic(clock: MusicClock): void {
    if (this.disposed || !this.track || !this.clock) return;
    this.clock = heard(clock, this.clock);
    const player = this.player;
    const ctx = this.buses?.ctx;
    if (!player || !ctx || this.hidden()) return;
    player.sync(this.audioTime(ctx, clock.ago), clock.position, clock.scale, ctx.currentTime);
    // The page came back (see onVisibility): the music goes on from where the game is now.
    if (this.timer === null) {
      this.tick();
      this.startTimer();
    }
  }

  stopMusic(fadeMs: number = DEFAULT_STOP_FADE_MS): void {
    this.track = null;
    this.clock = null;
    this.retirePlayer(Math.max(0, fadeMs) / 1000);
    this.stopTimer();
  }

  setTimeScale(scale: number): void {
    if (!Number.isFinite(scale) || scale <= 0) return;
    this.timeScale = scale;
    const ctx = this.buses?.ctx;
    // Music on the game's clock takes the scale from the clock.
    if (ctx && this.player && !this.clock) this.player.setTimeScale(scale, ctx.currentTime);
  }

  setVolumes(volumes: { sfx: number; music: number }): void {
    this.volumes = { sfx: volumes.sfx, music: volumes.music };
    const buses = this.buses;
    if (!buses) return;
    buses.sfx.gain.setTargetAtTime(sfxBusGain(volumes.sfx), buses.ctx.currentTime, 0.02);
    this.applyMusicGain();
  }

  setDucked(ducked: boolean): void {
    this.ducked = ducked;
    this.applyMusicGain();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.track = null;
    this.stopTimer();
    for (const cleanup of this.cleanups.splice(0)) cleanup();
    const buses = this.buses;
    this.buses = null;
    const player = this.player;
    this.player = null;
    if (!buses) return;
    // A short fade instead of a click, then the whole subgraph goes away.
    const now = buses.ctx.currentTime;
    for (const bus of [buses.sfx, buses.music]) {
      bus.gain.cancelScheduledValues(now);
      bus.gain.setTargetAtTime(0, now, 0.015);
    }
    player?.stop(now, 0.08);
    setTimeout(() => {
      player?.disconnect();
      buses.sfx.disconnect();
      buses.music.disconnect();
    }, 120);
  }

  // ---- internals ----

  private hidden(): boolean {
    return this.doc?.visibilityState === 'hidden';
  }

  /** Buses on the running shared context (rebuilt if the context was replaced). */
  private ensureBuses(): Buses | null {
    const ctx = this.host.runningContext();
    if (!ctx) return null;
    if (this.buses?.ctx === ctx) return this.buses;
    const output = this.host.output();
    const noise = this.host.noise();
    if (!output || !noise) return null;
    this.player = null;
    const sfx = ctx.createGain();
    sfx.gain.value = sfxBusGain(this.volumes.sfx);
    sfx.connect(output);
    const music = ctx.createGain();
    music.gain.value = this.musicGain();
    music.connect(output);
    this.buses = { ctx, sfx, music, noise };
    return this.buses;
  }

  private musicGain(othersTalking = this.host.remoteVoiceActive.peek()): number {
    return musicBusGain(this.volumes.music, this.ducked || othersTalking);
  }

  private applyMusicGain(othersTalking = this.host.remoteVoiceActive.peek()): void {
    const buses = this.buses;
    if (!buses) return;
    const target = this.musicGain(othersTalking);
    const current = buses.music.gain.value;
    // Duck fast, come back slowly.
    buses.music.gain.setTargetAtTime(target, buses.ctx.currentTime, target < current ? 0.06 : 0.3);
  }

  private onContextRunning(running: boolean): void {
    if (running && this.track && !this.player) this.startPlayer();
  }

  private onVisibility(): void {
    const player = this.player;
    const ctx = this.buses?.ctx;
    if (this.hidden()) {
      this.stopTimer();
      if (player && ctx) player.pause(ctx.currentTime);
      return;
    }
    if (!this.track) return;
    if (player && ctx) {
      // On the game's clock nothing is scheduled until the next frame's sync places the tune
      // again: resuming now would play its next step at once, before the attempt has begun
      // if it was hidden in the pause before one.
      if (this.clock) return;
      player.resume(ctx.currentTime + 0.05);
      this.startTimer();
    } else {
      this.startPlayer();
    }
  }

  /** (Re)starts the wanted track from its first bar, if it can play now. */
  private startPlayer(): void {
    this.retirePlayer(RESTART_FADE);
    const track = this.track;
    if (!track || this.disposed || this.hidden()) return;
    const buses = this.ensureBuses();
    if (!buses) return;
    const player = new MusicPlayer(
      buses.ctx,
      buses.music,
      buses.noise,
      this.song(track),
      track.bpm,
      { stats: this.musicStats },
    );
    const at = buses.ctx.currentTime;
    const clock = this.clock;
    if (clock) {
      // On the game's clock: where it is now, as last heard (the next frame corrects it). The
      // game moves on at most MAX_CLOCK_GUESS_MS without a frame: a tune placed after a long
      // gap (the sound unlocked right after the page came back) would land seconds into the
      // song and wait there in silence (a step is never played twice).
      const since = Math.min(MAX_CLOCK_GUESS_MS, Math.max(0, now() - clock.heardAt));
      const position = clock.position + (since / 1000) * clock.scale;
      player.sync(this.audioTime(buses.ctx, clock.ago), position, clock.scale, at);
    } else {
      player.setTimeScale(this.timeScale, at, true);
      player.start(at + 0.06);
    }
    this.player = player;
    this.tick();
    this.startTimer();
  }

  /**
   * The context time at which a sound is heard at the moment `agoMs` before now, as the
   * picture of that moment reaches the screen: the output latency (from the output timestamp
   * where the browser has it, else the latency it reports) and DISPLAY_LAG.
   */
  private audioTime(ctx: AudioContext, agoMs: number): number {
    let lag = (ctx.outputLatency || 0) + (ctx.baseLatency || 0);
    const stamp = typeof ctx.getOutputTimestamp === 'function' ? ctx.getOutputTimestamp() : null;
    if (stamp?.contextTime && stamp.performanceTime) {
      // The sample of contextTime is heard at performanceTime: what is heard right now.
      const heardNow = stamp.contextTime + (now() - stamp.performanceTime) / 1000;
      const measured = ctx.currentTime - heardNow;
      if (measured >= 0 && measured <= MAX_OUTPUT_LAG) lag = measured;
    }
    lag = Math.min(MAX_OUTPUT_LAG, Math.max(0, lag));
    // Smoothed: a jittery estimate would shake the beat.
    this.outputLag = this.outputLag === null ? lag : this.outputLag + (lag - this.outputLag) * 0.05;
    return ctx.currentTime - this.outputLag - agoMs / 1000 + DISPLAY_LAG;
  }

  /** Fades the current player out and lets it go. */
  private retirePlayer(fadeSeconds: number): void {
    const player = this.player;
    this.player = null;
    const ctx = this.buses?.ctx;
    if (!player || !ctx) return;
    player.stop(ctx.currentTime, fadeSeconds);
    setTimeout(() => player.disconnect(), fadeSeconds * 1000 + 100);
  }

  private song(track: MusicTrack): Song {
    const season = track.season ?? 'none';
    const key = `${track.seed}:${track.theme}:${season}:${track.boss ?? ''}`;
    let song = this.songs.get(key);
    if (!song) {
      song = composeSong(track.seed, track.theme, season, track.boss);
      if (this.songs.size >= 4) this.songs.clear();
      this.songs.set(key, song);
    }
    return song;
  }

  private startTimer(): void {
    if (this.timer === null) this.timer = setInterval(() => this.tick(), SCHEDULER_TICK_MS);
  }

  private stopTimer(): void {
    if (this.timer === null) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  private tick(): void {
    const player = this.player;
    const ctx = this.host.runningContext();
    if (!player || !ctx || ctx !== this.buses?.ctx) return;
    player.scheduleUntil(ctx.currentTime + SCHEDULER_LOOKAHEAD, ctx.currentTime);
  }
}

/** A copy of the engine's clock stamped with when it was heard (reusing `into`). */
function heard(clock: MusicClock, into?: HeardClock): HeardClock {
  const target = into ?? { position: 0, scale: 1, ago: 0, heardAt: 0 };
  target.position = clock.position;
  target.scale = clock.scale;
  target.ago = clock.ago;
  target.heardAt = now();
  return target;
}
